import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { PROJECT_TYPES, EASE, waLink } from "@/lib/site";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const EMPTY = { name: "", whatsapp: "", email: "", project_type: "", description: "" };

const maskPhone = (v) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const validate = (f) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Informe seu nome.";
  if (f.whatsapp.replace(/\D/g, "").length < 10) e.whatsapp = "Informe um WhatsApp válido com DDD.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Informe um e-mail válido.";
  if (!f.project_type) e.project_type = "Selecione o tipo de projeto.";
  if (f.description.trim().length < 10) e.description = "Conte um pouco mais sobre o projeto (mín. 10 caracteres).";
  return e;
};

const buildMessage = (f) =>
  `Olá! Conheci a WebStation pelo site e gostaria de solicitar um orçamento para um projeto.\n\n*Nome:* ${f.name}\n*WhatsApp:* ${f.whatsapp}\n*E-mail:* ${f.email}\n*Tipo de projeto:* ${f.project_type}\n*Descrição:* ${f.description}`;

const inputCls = "w-full rounded-2xl border border-white/10 bg-ws-bg/70 px-4 py-3.5 text-[15px] text-ws-text placeholder:text-ws-muted/70 outline-none transition-[border-color,box-shadow] duration-300 focus:border-ws-lime/70 focus:shadow-[0_0_0_4px_rgba(124,255,26,.12)]";

const Field = ({ label, error, children, id }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-sm font-medium text-ws-text">{label}</label>
    {children}
    {error && <p data-testid={`error-${id}`} className="mt-1.5 text-xs text-red-400">{error}</p>}
  </div>
);

const Success = ({ link, onReset }) => (
  <motion.div data-testid="contact-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="flex min-h-[460px] flex-col items-center justify-center text-center">
    <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.1 }} className="grid h-20 w-20 place-items-center rounded-full bg-ws-lime text-ws-bg shadow-[0_0_50px_-6px_rgba(124,255,26,.8)]">
      <CheckCircle2 size={38} />
    </motion.span>
    <h3 className="mt-8 text-2xl font-medium">Solicitação recebida!</h3>
    <p className="mt-3 max-w-sm text-ws-muted">Obrigado pelo contato. Abrimos o WhatsApp com os dados do seu projeto — é só enviar a mensagem para falar com a WebStation.</p>
    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
      <a data-testid="contact-success-whatsapp" href={link} target="_blank" rel="noopener noreferrer" className="rounded-full bg-ws-lime px-6 py-3 text-sm font-semibold text-ws-bg transition-colors hover:bg-ws-glow">Abrir WhatsApp novamente</a>
      <button data-testid="contact-success-reset" onClick={onReset} className="rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-ws-text transition-colors hover:border-ws-lime/60">Enviar outra solicitação</button>
    </div>
  </motion.div>
);

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sentLink, setSentLink] = useState(null);

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, project_type: e.detail }));
    window.addEventListener("ws:prefill", onPrefill);
    return () => window.removeEventListener("ws:prefill", onPrefill);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "whatsapp" ? maskPhone(e.target.value) : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const link = waLink(buildMessage(form));
    window.open(link, "_blank", "noopener");
    setSending(true);
    if (API) {
      try {
        await axios.post(`${API}/leads`, { ...form, email: form.email.trim() });
      } catch (err) {
        console.warn("Lead não salvo", err?.message);
      }
    }
    setSending(false);
    setSentLink(link);
  };

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      {sentLink ? (
        <Success key="ok" link={sentLink} onReset={() => { setForm(EMPTY); setSentLink(null); }} />
      ) : (
        <motion.form key="form" data-testid="contact-form" onSubmit={submit} noValidate exit={{ opacity: 0 }} className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Nome" id="name" error={errors.name}>
              <input id="name" data-testid="contact-input-name" className={inputCls} placeholder="Seu nome" value={form.name} onChange={set("name")} autoComplete="name" />
            </Field>
            <Field label="WhatsApp" id="whatsapp" error={errors.whatsapp}>
              <input id="whatsapp" data-testid="contact-input-whatsapp" className={inputCls} placeholder="(11) 90000-0000" inputMode="tel" value={form.whatsapp} onChange={set("whatsapp")} autoComplete="tel" />
            </Field>
          </div>
          <Field label="E-mail" id="email" error={errors.email}>
            <input id="email" type="email" data-testid="contact-input-email" className={inputCls} placeholder="voce@email.com" value={form.email} onChange={set("email")} autoComplete="email" />
          </Field>
          <Field label="Tipo de projeto" id="project_type" error={errors.project_type}>
            <div id="project_type" role="radiogroup" className="flex flex-wrap gap-2">
              {PROJECT_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={form.project_type === t}
                  data-testid={`contact-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setForm((f) => ({ ...f, project_type: t }))}
                  className={`rounded-full border px-4 py-2 text-sm transition-[background-color,border-color,color] duration-300 ${form.project_type === t ? "border-ws-lime bg-ws-lime font-semibold text-ws-bg" : "border-white/10 text-ws-muted hover:border-ws-lime/50 hover:text-ws-text"}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Descrição do projeto" id="description" error={errors.description}>
            <textarea id="description" data-testid="contact-input-description" rows={5} className={`${inputCls} resize-none`} placeholder="Conte sua ideia, o que você precisa e qual o seu objetivo." value={form.description} onChange={set("description")} />
          </Field>
          <motion.button whileTap={{ scale: 0.97 }} type="submit" disabled={sending} data-testid="contact-submit" className="btn-shine flex w-full items-center justify-center gap-2 rounded-full bg-ws-lime py-4 text-[15px] font-semibold text-ws-bg shadow-[0_10px_40px_-10px_rgba(124,255,26,.7)] transition-colors hover:bg-ws-glow disabled:opacity-70">
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
            Solicitar orçamento
          </motion.button>
          <p className="text-center text-xs text-ws-muted">Ao enviar, abriremos o WhatsApp com sua mensagem pronta para a WebStation.</p>
        </motion.form>
      )}
    </AnimatePresence>
  );
};
